import { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Drawer } from "./ui/custom/Drawer";
import { Bot, Send, User } from "lucide-react";
import { Input } from "./ui/input";
import { Message, useMockChat } from "@/hooks/useMockChat";
import { ProductItem } from "./ProductItem";
import { ScrollArea } from "./ui/scroll-area";
import { HenrySvg } from "./HenrySvg";
import { cn } from "@/lib/utils";

export function AiButton({
  className,
  showLabel = false,
  onClose,
}: {
  className?: string;
  showLabel?: boolean;
  onClose?: () => void;
}) {
  const [drawerIsOpen, setDrawerIsOpen] = useState<boolean>(false);

  return null;

  return (
    <>
      <Button
        onClick={(event) => {
          event.stopPropagation();
          setDrawerIsOpen(true);
        }}
        size="icon"
        className={cn(
          "h-12 w-12 rounded-lg bg-background hover:bg-background/90 text-foreground shadow-lg transition-all duration-200 hover:scale-110",
          className
        )}
      >
        <div className="size-6 flex items-center justify-center rounded-lg aspect-square">
          <HenrySvg />
        </div>
      </Button>

      {showLabel && (
        <span
          className="absolute top-1/2 left-0 translate-x-14 text-left -translate-y-1/2 text-sm  whitespace-nowrap bg-background py-1 px-3 rounded-lg text-foreground"
          onClick={(event) => {
            event.stopPropagation();
            setDrawerIsOpen(true);
          }}
        >
          Chat with Henry
        </span>
      )}
      <ControlledDrawer
        isOpen={drawerIsOpen}
        handleClose={() => {
          setDrawerIsOpen(false);
          onClose?.();
        }}
      />
    </>
  );
}

function ControlledDrawer({
  isOpen,
  handleClose,
}: {
  isOpen: boolean;
  handleClose: () => void;
}) {
  return (
    <Drawer
      isOpen={isOpen}
      closeDrawer={handleClose}
      classes={{
        root: "!z-[19]",
        paper: "rounded-t-2xl !overflow-visible !bg-transparent",
      }}
    >
      <div className="z-[1]">{isOpen && <Chat />}</div>
    </Drawer>
  );
}

function Chat() {
  const { messages, input, handleInputChange, handleSubmit, isLoading } =
    useMockChat();

  return (
    <div className="bg-background rounded-t-2xl h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-card">
        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <HenrySvg />
        </div>
        <div>
          <h1 className="font-semibold text-lg">AI Bartender</h1>
          <p className="text-sm text-muted-foreground">
            Find your perfect drink
          </p>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4 max-h-[70vh] overflow-auto">
        <div className="space-y-4">
          {messages.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-4 text-center">
                <div className="w-8 h-8 mx-auto mb-2 text-muted-foreground">
                  <HenrySvg />
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Hi! I'm your AI bartender. Ask me about drinks!
                </p>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p>• "What's a good IPA that's not too hazy?"</p>
                  <p>• "Recommend a light beer under $8"</p>
                  <p>• "Something hoppy but not too strong"</p>
                </div>
              </CardContent>
            </Card>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${
                message.role === "user" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted"
                }`}
              >
                {message.role === "user" ? (
                  <User className="w-4 h-4" />
                ) : (
                  <Bot className="w-4 h-4" />
                )}
              </div>

              <div
                className={`flex-1 ${
                  message.role === "user" ? "text-right" : "text-left"
                }`}
              >
                <MessageContent message={message} />
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <HenrySvg />
              </div>
              <div className="flex-1">
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                      <div
                        className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      />
                      <div
                        className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
      <div className="p-4 border-t bg-card">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            value={input}
            onChange={handleInputChange}
            placeholder="Ask about drinks..."
            className="flex-1"
            disabled={isLoading}
            autoFocus
          />
          <Button
            type="submit"
            size="icon"
            disabled={isLoading || !input.trim()}
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}

interface MessageContentProps {
  message: Message;
}

export function MessageContent({ message }: MessageContentProps) {
  if (message.role === "user") {
    return (
      <Card className="bg-primary text-primary-foreground">
        <CardContent className="p-3">
          <p className="text-sm">{message.content}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {message.content && (
        <Card>
          <CardContent className="p-3">
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          </CardContent>
        </Card>
      )}

      {message.parts?.map((part, index) => {
        if (part.type === "tool-result" && part.toolResult) {
          const { products, reasoning } = part.toolResult;
          return (
            <div key={index} className="space-y-2">
              {reasoning && (
                <Card>
                  <CardContent className="!p-0">
                    <p className="p-3 text-sm text-muted-foreground">
                      {reasoning}
                    </p>
                    <div className="space-y-2">
                      {products.map((product: any) => (
                        <div
                          className="overflow-hidden rounded-md shadow-sm"
                          key={product.id}
                        >
                          <ProductItem item={product} />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}
